import { useEffect, useState } from 'react';
import {
  CommonArea,
  PropertyCommonAreasShape,
} from 'src/SharedCustomHooks/UseCommonAreas/useCommonAreas';

export interface UseCommonAreaFormStateOptions {
  commonAreas: CommonArea[];
  propertyCommonAreas: PropertyCommonAreasShape;
}

export interface UseCommonAreaFormStateReturn {
  selectedAreas: CommonArea[];
  commonAreasList: CommonArea[];
  handleAreas: (id: any, value: any, area: CommonArea) => void;
  onEnterText: (value: string, id: any) => void;
  onAddArea: (id: any) => void;
  buildSavePayload: () => CommonArea[];
}

const OTHER_PLACEHOLDER: CommonArea = {
  id: 'Other',
  type: 'Other',
  name: 'Other',
  customLabel: '',
};

export const useCommonAreaFormState = ({
  commonAreas,
  propertyCommonAreas,
}: UseCommonAreaFormStateOptions): UseCommonAreaFormStateReturn => {
  const [selectedAreas, setSelectedAreas] = useState<CommonArea[]>([]);
  const [commonAreasList, setCommonAreasList] = useState<CommonArea[]>([]);

  useEffect(() => {
    const existingSelectedAreas: CommonArea[] =
      propertyCommonAreas?.commonAreas ?? [];

    const savedOtherAreas: CommonArea[] = (
      propertyCommonAreas?.otherCommonAreas ?? []
    ).filter((area: CommonArea) => area.type === 'Other');

    const fullAreasList: CommonArea[] = [
      ...(commonAreas ?? []),
      ...savedOtherAreas,
      OTHER_PLACEHOLDER,
    ];

    setSelectedAreas(existingSelectedAreas);
    setCommonAreasList(fullAreasList);
  }, [commonAreas, propertyCommonAreas]);

  const handleAreas = (id: any, value: any, area: CommonArea): void => {
    const isAlreadySelected: boolean = selectedAreas.some(
      (d: CommonArea) => d.id === id
    );

    if (!isAlreadySelected) {
      const areaToAdd: CommonArea =
        area.name === 'Other' ? { ...OTHER_PLACEHOLDER } : area;

      const nextSelectedAreas: CommonArea[] = [...selectedAreas, areaToAdd];

      setSelectedAreas(nextSelectedAreas);
      return;
    }

    const nextSelectedAreas: CommonArea[] = selectedAreas.filter(
      (a: CommonArea) => a.id !== id
    );

    const isRemovingCustomArea: boolean = Boolean(value && value.new);

    if (isRemovingCustomArea) {
      const nextCommonAreasList: CommonArea[] = commonAreasList.filter(
        (a: CommonArea) => a.id !== id
      );

      setCommonAreasList(nextCommonAreasList);
    }

    setSelectedAreas(nextSelectedAreas);
  };

  const onEnterText = (value: string, id: any): void => {
    const nextSelectedAreas: CommonArea[] = selectedAreas.map(
      (s: CommonArea) => (s.id === id ? { ...s, customLabel: value } : s)
    );

    setSelectedAreas(nextSelectedAreas);
  };

  const onAddArea = (id: any): void => {
    const sourceArea: CommonArea | undefined = selectedAreas.find(
      (item: CommonArea) => item.id === id
    );

    if (!sourceArea) {
      return;
    }

    const trimmedLabel: string = (sourceArea.customLabel || '').trim();

    if (!trimmedLabel) {
      alert('Please Enter custom label name for adding other check box');
      return;
    }

    const min = 1;
    const max = 100;
    const randomId: number = min + Math.random() * (max - min);

    const newCustomArea: CommonArea = {
      id: randomId,
      new: true,
      name: sourceArea.name,
      type: sourceArea.type,
      customLabel: trimmedLabel,
    };

    const areasBeforeOtherPlaceholder: CommonArea[] = commonAreasList.filter(
      (area: CommonArea) => area.id !== 'Other'
    );
    const otherPlaceholderRow: CommonArea | undefined = commonAreasList.find(
      (area: CommonArea) => area.id === 'Other'
    );

    const nextCommonAreasList: CommonArea[] = otherPlaceholderRow
      ? [...areasBeforeOtherPlaceholder, newCustomArea, otherPlaceholderRow]
      : [...areasBeforeOtherPlaceholder, newCustomArea];

    const selectedAreasWithClearedOtherText: CommonArea[] = selectedAreas.map(
      (area: CommonArea) =>
        area.id === 'Other' ? { ...area, customLabel: '' } : area
    );

    const nextSelectedAreas: CommonArea[] = [
      ...selectedAreasWithClearedOtherText,
      newCustomArea,
    ];

    setCommonAreasList(nextCommonAreasList);
    setSelectedAreas(nextSelectedAreas);
  };

  const buildSavePayload = (): CommonArea[] => {
    const realSelectedAreas: CommonArea[] = selectedAreas.filter(
      (area: CommonArea) => area.id !== 'Other'
    );

    const payloadAreas: CommonArea[] = realSelectedAreas.map(
      (area: CommonArea) => {
        const newAreaPayload: CommonArea = {
          name: area.name,
          type: area.type,
          customLabel: area.customLabel,
        } as CommonArea;

        return area.new ? newAreaPayload : area;
      }
    );

    return payloadAreas;
  };

  return {
    selectedAreas,
    commonAreasList,
    handleAreas,
    onEnterText,
    onAddArea,
    buildSavePayload,
  };
};

export default useCommonAreaFormState;
